/** Admin workspace for the personal conversations members start in their profile. */

import {
  EmptyState,
  ErrorState,
  LoadingState,
  RADIUS,
  SHADOW,
  WithHalfdot,
} from "@elyxeer/ui";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { AdminChatConversation } from "~/components/chat/AdminChatConversation";
import { ChatThreadList } from "~/components/chat/ChatThreadList";
import { Screen } from "~/components/ui/Screen";
import { useLocale } from "~/i18n";
import { fetchAdminChatThreads } from "~/lib/api";
import { uniqueChatThreads } from "~/lib/chat";
import { queryKeys } from "~/lib/query-client";

const THREAD_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const THREAD_POLL_MS = 10_000;

export function ChatRoute() {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const navigate = useNavigate();
  const { threadId } = useParams<{ threadId?: string }>();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [unreadOnly, setUnreadOnly] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput.trim() || undefined);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const query = useInfiniteQuery({
    queryKey: queryKeys.chatThreads({ search, unreadOnly }),
    queryFn: ({ pageParam, signal }) =>
      fetchAdminChatThreads({
        search,
        unreadOnly,
        limit: THREAD_PAGE_SIZE,
        offset: pageParam,
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    refetchInterval: THREAD_POLL_MS,
  });

  const threads = uniqueChatThreads(query.data?.pages ?? []);
  const total = query.data?.pages[0]?.total ?? 0;
  const hasFilter = Boolean(search || unreadOnly);
  const threadDateOptions = {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  } as const;

  return (
    <Screen maxWidth="xl">
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="H1" sx={{ color: "text.primary" }}>
          <WithHalfdot size={11}>{t("chat.title")}</WithHalfdot>
        </Typography>
        <Typography variant="T2" sx={{ color: "text.secondary", marginTop: 1 }}>
          {t("chat.subtitle")}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          marginBottom: 2,
        }}
      >
        <TextField
          label={t("chat.search")}
          type="search"
          size="small"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            },
            htmlInput: { "data-testid": "chat.search" },
          }}
          sx={{ width: { xs: "100%", sm: 360 } }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={unreadOnly}
              onChange={(event) => setUnreadOnly(event.target.checked)}
              data-testid="chat.unread-only"
            />
          }
          label={t("chat.unreadOnly")}
        />
      </Box>

      <Box
        data-testid="chat.workspace"
        sx={{
          display: "flex",
          minHeight: { xs: 480, md: 560 },
          height: { xs: "calc(100dvh - 300px)", md: "calc(100dvh - 300px)" },
          maxHeight: 760,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: `${RADIUS.lg}px`,
          backgroundColor: "background.paper",
          boxShadow: SHADOW.card,
        }}
      >
        <Box
          data-testid="chat.thread-list"
          sx={{
            width: { xs: "100%", md: 380 },
            flexShrink: 0,
            minWidth: 0,
            display: { xs: threadId ? "none" : "flex", md: "flex" },
            flexDirection: "column",
            borderRight: { xs: 0, md: "1px solid" },
            borderColor: { md: "divider" },
          }}
        >
          {!query.isPending && !query.isError && threads.length > 0 ? (
            <Typography
              variant="L3"
              sx={{ color: "text.muted", paddingInline: 2, paddingBlock: 1 }}
            >
              {t("chat.count", { loaded: threads.length, total })}
            </Typography>
          ) : null}

          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {query.isPending ? (
              <LoadingState />
            ) : query.isError ? (
              <ErrorState
                message={t("chat.errors.load")}
                onRetry={() => void query.refetch()}
              />
            ) : threads.length === 0 ? (
              <EmptyState
                message={t(hasFilter ? "chat.emptyFilter" : "chat.empty")}
              />
            ) : (
              <ChatThreadList
                threads={threads}
                selectedId={threadId}
                formatDate={(value) => formatDate(value, threadDateOptions)}
                onSelect={(id) => void navigate(`/chat/${id}`)}
              />
            )}
          </Box>

          {query.hasNextPage ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: 1.5,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Button
                size="small"
                onClick={() => void query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
                data-testid="chat.load-more"
              >
                {t("chat.loadMore")}
              </Button>
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: { xs: threadId ? "flex" : "none", md: "flex" },
          }}
        >
          {threadId ? (
            <AdminChatConversation
              key={threadId}
              threadId={threadId}
              onBack={() => void navigate("/chat")}
            />
          ) : (
            <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
              <EmptyState message={t("chat.selectThread")} />
            </Box>
          )}
        </Box>
      </Box>
    </Screen>
  );
}
